import { useCallback, useEffect, useRef, useState } from 'react';

// Space kept free inside Google's limit (rounding, shadow).
const SAFETY_PX = 8;

/**
 * Keeps an InfoWindow's scrollable content within the height Google allows
 * the popup. Google sets that limit (inline max-height on .gm-style-iw-c)
 * from the map size and the anchor position and clips anything taller, so
 * a fixed viewport-based cap can leave the bottom of the content cut off.
 *
 * Returns [refCallback, maxHeightPx]: put the ref on the scroll container
 * and use maxHeightPx as its max-height (fallbackPx until measured).
 */
export default function useInfoWindowFit(fallbackPx) {
  const [maxHeight, setMaxHeight] = useState(fallbackPx);
  const cleanupRef = useRef(null);

  const ref = useCallback((el) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!el) return;

    let frame = 0;
    let observer = null;
    const fit = () => {
      const iw = el.closest('.gm-style-iw-c');
      if (!iw) return false;
      const limit = parseFloat(iw.style.maxHeight);
      if (!Number.isFinite(limit) || limit <= 0) return true;
      const header = iw.querySelector('.gm-style-iw-chr');
      const cs = window.getComputedStyle(iw);
      const used = (header ? header.offsetHeight : 0)
        + (parseFloat(cs.paddingTop) || 0)
        + (parseFloat(cs.paddingBottom) || 0);
      setMaxHeight(Math.max(160, Math.floor(limit - used - SAFETY_PX)));
      return true;
    };
    // Content mounts before Google attaches it to the popup; retry briefly.
    let tries = 0;
    const attach = () => {
      const iw = el.closest('.gm-style-iw-c');
      if (iw) {
        fit();
        observer = new MutationObserver(fit);
        observer.observe(iw, { attributes: true, attributeFilter: ['style'] });
      } else if (tries < 30) {
        tries += 1;
        frame = window.requestAnimationFrame(attach);
      }
    };
    attach();
    window.addEventListener('resize', fit);
    cleanupRef.current = () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, []);

  useEffect(() => () => {
    if (cleanupRef.current) cleanupRef.current();
  }, []);

  return [ref, maxHeight];
}
